import React from "react";
import {
  Stack,
  Typography,
  TextField,
  Chip,
  Button,
  Checkbox,
} from "@mui/material";
import {
  InputControl,
  LimitedTag,
  PageListLayout,
  ServerPaginatedtable as ServerPaginatedTable,
} from "../../../shared";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useSubmissionList } from "../hooks";

export const SubmissionsList = () => {
  const navigate = useNavigate();
  const {
    submissionListQuery,
    search,
    setSearch,
    storeFilter,
    setStoreFilter,
    handleClearFilter,
    pagination,
    setPagination,
    sorting,
    setSorting,
    disableClearButton,
    stockTakeStatuses,
    storeLists,
    isStoreAdmin,
  } = useSubmissionList();

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("month", {
      header: () => "Month",
      cell: ({ getValue }) => getValue(),
    }),
    columnHelper.accessor("submission_date", {
      header: () => "Date of Submission",
      enableSorting: false,
      cell: ({ getValue }) => getValue() || "--",
    }),
    columnHelper.accessor("submitted_by_name", {
      header: () => "Submitted By",
      cell: ({ getValue }) => getValue() || "--",
    }),
    columnHelper.accessor("store_name", {
      header: () => "Store Name",
      cell: ({ getValue }) => getValue(),
      ...(isStoreAdmin && { hidden: true }),
    }),
    columnHelper.accessor("total_stock_amount", {
      header: () => "Stock Amount",
      cell: ({ getValue }) =>
        typeof getValue() === "number" ? `$${getValue()}` : "--",
    }),
    columnHelper.accessor("status", {
      header: () => "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        const actualStatus = stockTakeStatuses.find(
          (s) => s.value === status,
        )?.label;
        const color = status === 3 ? "primary" : "default";
        const variant = status === 2 ? "draft" : "filled";

        return actualStatus ? (
          <Chip
            label={actualStatus}
            color={color}
            size="small"
            variant={variant}
            sx={{ transform: "capitalize" }}
          />
        ) : (
          "--"
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      cell: ({ row }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
        >
          <img
            alt="details"
            src="/icons/arrow_forward.png"
            onClick={() => navigate(`details/${row?.original?.id}`)}
            style={{ cursor: "pointer" }}
          />
        </Stack>
      ),
    }),
  ];

  const breadcrumbs = [
    <Typography key="submissions" sx={{ fontSize: "14px", fontWeight: 400 }}>
      Stock Submissions
    </Typography>,
  ];

  // Extract pagination info from API response
  const apiPagination = submissionListQuery.data?.pagination ?? {
    total: 0,
    lastPage: 1,
  };

  return (
    <PageListLayout breadcrumbs={breadcrumbs} sx={{ p: 0 }}>
      <ServerPaginatedTable
        manualSortBy={true}
        defaultData={submissionListQuery.data?.submissions || []}
        columns={columns}
        headerText="Stock Submissions"
        search={search}
        pagination={pagination}
        setPagination={setPagination}
        paginationProps={apiPagination}
        onSearchChange={setSearch}
        isLoading={submissionListQuery.isLoading}
        onRowLink={(e) => `details/${e.id}`}
        rightContent={<div></div>}
        sorting={sorting}
        setSorting={setSorting}
        tableHeaderContent={
          !isStoreAdmin && (
            <Stack sx={{ flexDirection: "row", gap: "6px", width: "100%" }}>
              <InputControl
                type="dropdown"
                variant="filter"
                limitTags={1}
                size="small"
                renderTags={(value) => (
                  <LimitedTag value={value} autocompleteWidth="250px" />
                )}
                multiple
                options={storeLists}
                value={storeFilter}
                onChange={setStoreFilter}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option?.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Store"
                    sx={{
                      width: "250px",
                      "& .MuiInputBase-root": {
                        height: "40px",
                        minHeight: "40px",
                      },
                    }}
                  />
                )}
              />
              <Button
                variant="text"
                disabled={disableClearButton}
                onClick={handleClearFilter}
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                Clear All
              </Button>
            </Stack>
          )
        }
      />
    </PageListLayout>
  );
};
